import {
  ClaimAssigneeRelationshipCanon,
  ContactAssignee,
  GetAssignmentsForAssignableResponse,
  JobAssigneeRelationship,
  VendorRatesAssigneeRelationship,
} from '@eigtech/assignments-types'
import { Book } from '@eigtech/contacts-types'
import { JobId } from '@eigtech/restoration-types'
import { ensureJobCorn } from '@eigtech/restoration-util'
import { Corn } from '@eigtech/shared-corn'
import { ensureClaimCorn } from '@eigtech/shared-corn-helper'
import {
  useGetAssignmentsForAssignable,
  useGetAssignmentsForAssignableSuspense,
} from '@eigtech/ui-shared-assignments'
import { last } from 'lodash-es'
import { useMemo } from 'react'

type ClaimContactAssignee = Exclude<ClaimAssigneeRelationshipCanon, 'job'>

export type UseGetContactOfTypeFromClaimProps = { contactType: ClaimContactAssignee } & (
  | { claimNumber: string }
  | { book: Book | undefined }
)

export function useGetContactOfTypeFromClaim(props: UseGetContactOfTypeFromClaimProps) {
  const claimNumber =
    'claimNumber' in props
      ? props.claimNumber
      : (props.book?.metadata.type === 'claim' && last(props.book.bookId.split(':'))) || ''

  const claimCorn = ensureClaimCorn(claimNumber)

  return useGetContactOfTypeFromEntity({ contactType: props.contactType, entityCorn: claimCorn })
}

//

type JobContactAssignee = JobAssigneeRelationship

export type UseGetContactOfTypeFromJobProps = { contactType: JobContactAssignee } & (
  | { jobId: JobId }
  | { book: Book | undefined }
)

export function useGetContactOfTypeFromJob(props: UseGetContactOfTypeFromJobProps) {
  const jobId =
    'jobId' in props
      ? props.jobId
      : (props.book?.metadata.type === 'claim' && last(props.book.bookId.split(':'))) || ''

  const jobCorn = ensureJobCorn(jobId)

  return useGetContactOfTypeFromEntity({ contactType: props.contactType, entityCorn: jobCorn })
}

//

export type UseGetContactOfTypeFromEntityProps = {
  contactType: ClaimContactAssignee | JobContactAssignee | VendorRatesAssigneeRelationship
  entityCorn: Corn
}

export function useGetContactOfTypeFromEntity({
  contactType,
  entityCorn,
}: UseGetContactOfTypeFromEntityProps) {
  const query = useGetAssignmentsForAssignable(entityCorn)

  const contacts = useMemo(
    () => getContactOfTypeFromEntity({ assignments: query.data, contactType }),
    [contactType, query.data]
  )

  return {
    query,
    contacts,
  }
}

export function useGetContactOfTypeFromEntitySuspense({
  contactType,
  entityCorn,
}: UseGetContactOfTypeFromEntityProps) {
  const query = useGetAssignmentsForAssignableSuspense(entityCorn)

  const contacts = useMemo(
    () => getContactOfTypeFromEntity({ assignments: query.data, contactType }),
    [contactType, query.data]
  )

  return {
    query,
    contacts,
  }
}

export type GetContactOfTypeFromEntityProps = {
  assignments: GetAssignmentsForAssignableResponse | undefined
  contactType: ClaimContactAssignee | JobContactAssignee | VendorRatesAssigneeRelationship
}

export function getContactOfTypeFromEntity({
  assignments,
  contactType,
}: GetContactOfTypeFromEntityProps) {
  return assignments
    ?.filter((assignment) => assignment.assignable.assigneeRelationship === contactType)
    .map((assignment) => (assignment.assignee as ContactAssignee).contact)
}
